<template>
  <div class="interaction-overlay">
    <!-- Animated mouse cursor -->
    <div 
      v-if="showCursor"
      class="animated-cursor"
      :style="cursorStyles"
    >
      <div class="cursor-icon">🖱️</div>
      <div 
        v-if="interactionData.description"
        class="cursor-tooltip"
      >
        {{ interactionData.description }}
      </div>
    </div>

    <!-- Click ripple effect -->
    <div 
      v-if="showRipple"
      class="click-ripple"
      :style="rippleStyles"
    ></div>

    <!-- Drag trail -->
    <svg 
      v-if="showTrail && dragPath.length > 1"
      class="drag-trail"
      width="100%"
      height="100%"
    >
      <path
        :d="pathData"
        stroke="#007bff"
        stroke-width="3"
        fill="none"
        stroke-linecap="round"
        stroke-dasharray="5,5"
        class="trail-path"
      />
    </svg>

    <!-- Element highlight -->
    <div 
      v-if="highlightElement"
      class="element-highlight"
      :style="highlightStyles"
    ></div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import type { PecTutorConfig } from '../types';

// Props
interface Props {
  config: PecTutorConfig;
  interactionData?: {
    type: string;
    selector?: string;
    from?: string;
    to?: string;
    description?: string;
    options?: Record<string, unknown>;
    duration?: number;
    onComplete?: () => void;
  } | null;
}

const props = defineProps<Props>();

// Emits
/*
const emit = defineEmits<{
  complete: [];
}>();
*/

// Reactive state
const showCursor = ref(false);
const showRipple = ref(false);
const showTrail = ref(false);
const highlightElement = ref<HTMLElement | null>(null);

const cursorPosition = ref({ x: 0, y: 0 });
const ripplePosition = ref({ x: 0, y: 0 });
const dragPath = ref<Array<{ x: number; y: number }>>([]);

// Computed properties
const cursorStyles = computed(() => ({
  left: `${cursorPosition.value.x}px`,
  top: `${cursorPosition.value.y}px`,
  transform: 'translate(-50%, -50%)',
}));

const rippleStyles = computed(() => ({
  left: `${ripplePosition.value.x}px`,
  top: `${ripplePosition.value.y}px`,
  transform: 'translate(-50%, -50%)',
}));

const highlightStyles = ref<Record<string, string>>({});
let scrollHandler: (() => void) | null = null;

const pathData = computed(() => {
  if (dragPath.value.length < 2) return '';
  
  let d = `M ${dragPath.value[0].x} ${dragPath.value[0].y}`;
  for (let i = 1; i < dragPath.value.length; i++) {
    d += ` L ${dragPath.value[i].x} ${dragPath.value[i].y}`;
  }
  return d;
});

// Methods
const findElement = (selector: string): HTMLElement | null => {
  try {
    return document.querySelector(selector);
  } catch {
    console.warn('PecTutor: Invalid selector:', selector);
    return null;
  }
};

const getElementCenter = (element: HTMLElement): { x: number; y: number } => {
  const rect = element.getBoundingClientRect();
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2
  };
};

const animateCursorToElement = async (selector: string): Promise<void> => {
  const element = findElement(selector);
  if (!element) {
    console.warn('PecTutor: Element not found:', selector);
    return;
  }

  const target = getElementCenter(element);
  const current = cursorPosition.value;
  
  // Highlight the target element
  highlightElement.value = element;
  
  return new Promise((resolve) => {
    const duration = 1000; // 1 second animation
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function (ease-out)
      const eased = 1 - Math.pow(1 - progress, 3);
      
      cursorPosition.value = {
        x: current.x + (target.x - current.x) * eased,
        y: current.y + (target.y - current.y) * eased
      };
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        resolve();
      }
    };
    
    animate();
  });
};

const simulateClick = async (position: { x: number; y: number }): Promise<void> => {
  // Show ripple effect
  ripplePosition.value = position;
  showRipple.value = true;
  
  // Hide ripple after animation
  setTimeout(() => {
    showRipple.value = false;
  }, 600);
};

const simulateDrag = async (from: { x: number; y: number }, to: { x: number; y: number }): Promise<void> => {
  dragPath.value = [from];
  showTrail.value = true;
  
  const duration = 1500; // 1.5 seconds
  const startTime = Date.now();
  
  return new Promise((resolve) => {
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function
      const eased = 1 - Math.pow(1 - progress, 2);
      
      const currentPoint = {
        x: from.x + (to.x - from.x) * eased,
        y: from.y + (to.y - from.y) * eased
      };
      
      cursorPosition.value = currentPoint;
      dragPath.value.push({ ...currentPoint });
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Clear trail after delay
        setTimeout(() => {
          showTrail.value = false;
          dragPath.value = [];
        }, 1000);
        resolve();
      }
    };
    
    animate();
  });
};

const clearHighlight = () => {
  highlightElement.value = null;
};

const updateHighlightStyles = () => {
  if (!highlightElement.value) {
    highlightStyles.value = {};
    return;
  }
  const rect = highlightElement.value.getBoundingClientRect();
  highlightStyles.value = {
    left: `${rect.left - 5}px`,
    top: `${rect.top - 5}px`,
    width: `${rect.width + 10}px`,
    height: `${rect.height + 10}px`,
    opacity: '1',
    transition: 'all 0.3s ease',
  };
};

// Watch for interaction changes
watch(() => props.interactionData, async (newData, oldData) => {
  // Clean up previous interaction's listeners
  if (oldData && scrollHandler) {
    window.removeEventListener('scroll', scrollHandler);
    scrollHandler = null;
  }

  if (!newData) {
    return;
  }
  
  showCursor.value = true;
  
  try {
    switch (newData.type) {
      case 'click': {
        if (newData.selector) {
          const element = findElement(newData.selector);
          if (element) {
            await animateCursorToElement(newData.selector);
            await simulateClick(cursorPosition.value);
            
            // Actually perform the click
            element.click();
          } else {
            console.warn(`PecTutor: Click failed, element not found for selector: ${newData.selector}`);
          }
        }
        break;
      }
      case 'drag': {
        if (newData.from && newData.to) {
          const fromElement = findElement(newData.from);
          const toElement = findElement(newData.to);
          
          if (fromElement && toElement) {
            const fromPos = getElementCenter(fromElement);
            const toPos = getElementCenter(toElement);
            
            cursorPosition.value = fromPos;
            await simulateDrag(fromPos, toPos);
          }
        }
        break;
      }
      case 'highlight': {
        if (newData.selector) {
          const element = findElement(newData.selector);
          if (element) {
            highlightElement.value = element;

            // Check if element is in viewport
            const rect = element.getBoundingClientRect();
            const isInViewport = (
              rect.top >= 0 &&
              rect.left >= 0 &&
              rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
              rect.right <= (window.innerWidth || document.documentElement.clientWidth)
            );

            if (!isInViewport) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              // Wait for the smooth scroll to finish
              await new Promise(resolve => setTimeout(resolve, 500));
            }

            // Set initial position and start listening for scroll
            updateHighlightStyles();
            scrollHandler = updateHighlightStyles;
            window.addEventListener('scroll', scrollHandler, { passive: true });

            // Wait for the specified duration
            await new Promise(resolve => setTimeout(resolve, newData.duration || 2000));
          } else {
            console.warn(`PecTutor: Highlight element not found: ${newData.selector}`);
          }
        }
        break;
      }
    }
  } catch (error) {
    console.error('PecTutor interaction error:', error);
  } finally {
    showCursor.value = false;
    highlightStyles.value = { ...highlightStyles.value, opacity: '0' };
    
    // Clean up scroll listener
    if (scrollHandler) {
      window.removeEventListener('scroll', scrollHandler);
      scrollHandler = null;
    }
    
    // Delay completion to allow for fade-out animation
    setTimeout(() => {
      clearHighlight();
      if (props.interactionData?.onComplete) {
        props.interactionData.onComplete();
      }
    }, 300);
  }
}, { immediate: true });

// Lifecycle
onMounted(() => {
  // Initialize cursor position to center of screen
  cursorPosition.value = {
    x: window.innerWidth / 2,
    y: window.innerHeight / 2
  };
});

onUnmounted(() => {
  clearHighlight();
});
</script>

<style scoped>
.interaction-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: 9998;
}

/* Animated cursor */
.animated-cursor {
  position: absolute;
  pointer-events: none;
  z-index: 10;
  transition: none;
}

.cursor-icon {
  font-size: 24px;
  filter: drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.3));
  animation: cursorPulse 2s infinite;
}

.cursor-tooltip {
  position: absolute;
  top: -40px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  font-family: system-ui, sans-serif;
}

.cursor-tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 5px solid transparent;
  border-top-color: rgba(0, 0, 0, 0.8);
}

/* Click ripple effect */
.click-ripple {
  position: absolute;
  width: 20px;
  height: 20px;
  border: 3px solid var(--pec-tutor-primary, #007bff);
  border-radius: 50%;
  pointer-events: none;
  animation: rippleExpand 0.6s ease-out;
}

/* Drag trail */
.drag-trail {
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: 1;
}

.trail-path {
  animation: trailDash 2s linear infinite;
}

/* Element highlight */
.element-highlight {
  position: absolute;
  border: 3px solid var(--pec-tutor-primary, #007bff);
  border-radius: 6px;
  background: rgba(0, 123, 255, 0.1);
  pointer-events: none;
  animation: highlightPulse 2s infinite;
  transition: all 0.3s ease;
}

/* Animations */
@keyframes cursorPulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

@keyframes rippleExpand {
  0% {
    width: 20px;
    height: 20px;
    opacity: 1;
  }
  100% {
    width: 60px;
    height: 60px;
    opacity: 0;
  }
}

@keyframes trailDash {
  0% { stroke-dashoffset: 0; }
  100% { stroke-dashoffset: 20; }
}

@keyframes highlightPulse {
  0%, 100% { 
    border-color: var(--pec-tutor-primary, #007bff);
    box-shadow: 0 0 0 0 rgba(0, 123, 255, 0.4);
  }
  50% { 
    border-color: #0056b3;
    box-shadow: 0 0 0 8px rgba(0, 123, 255, 0.1);
  }
}
</style>
